import React from 'react';
import { Menu } from 'antd';
import { ModalHandler } from 'app/components/panels/ModalHandler';
import { MeasureType } from 'app/model/CORE';
import {
    AddToCustomLayerEvent,
    NewCustomLayerEvent,
    RemoveFromCustomLayerEvent,
    RemoveFromWorkspaceLayerEvent,
    ShowModalEvent,
} from 'app/common/eventbus/Events';
import AppContext from 'app/common/context/AppContext';
import { CustomLayerUtils, MapLayer, MapLayerUtils, MapObjectType } from 'app/components/dashboard/network/map/Map';
import Utils from 'app/common/util/Utils';
import { Flex } from 'app/components/common/Flex';
import { MeasureLoadBar } from 'app/components/dashboard/network/flows/svg/MeasureLoadBar';
import { ObjectTypeContextMenu } from 'app/components/dashboard/network/flows/menu/MapContextMenu';
import { NiaMeasureImpact } from 'app/model/SOL49';
import { MapSelectionValues, NewLayerPopup } from 'app/components/dashboard/network/map/MapSelectionPopup';

interface MapMeasureImpactContextMenuProps extends ObjectTypeContextMenu {
    measures: Array<NiaMeasureImpact>;
    workspaceMeasureIds: Array<string>;
    onClose(): void;
}

const ACTION_OPEN_EDIT = 'open-edit';
const ACTION_SLOT_LIST = 'slot-list';
const ACTION_OP_LOG = 'oplog';
const ACTION_INTERACTION_STUDY = 'interaction-study';
const ACTION_CREATE_CUSTOM_LAYER = 'create-custom-layer';
const ACTIONS = [
    ACTION_OPEN_EDIT,
    ACTION_SLOT_LIST,
    ACTION_OP_LOG,
    ACTION_INTERACTION_STUDY,
    ACTION_CREATE_CUSTOM_LAYER,
];

export class MapMeasureImpactContextMenu extends React.PureComponent<MapMeasureImpactContextMenuProps> {
    render() {
        return (
            <Menu
                key="map-measure-impact-context-menu"
                items={this.getMenuItems()}
                onClick={this.handleOnClick}
                triggerSubMenuAction="click"
            />
        );
    }

    getMenuItems = () => {
        const subMenu = this.props.measures?.map(measure => ({
            label: this.getMeasureNameLabel(measure),
            key: measure.regulId,
            children: [
                {
                    label: measure.regulId,
                    key: `measureload-${measure.regulId}`,
                    className: 'menuItemLoadGraph',
                    disabled: true, //otherwise when clicking it hides the menu
                },
                { key: 'divider-1', type: 'divider' },
                { key: this.actionKey(ACTION_OPEN_EDIT, measure.regulId), label: `Open/edit ${measure.regulId}` },
                {
                    key: this.actionKey(ACTION_SLOT_LIST, measure.regulId),
                    disabled: !AppContext.isNmvpBaseline(),
                    label: 'Show slot list',
                },
                { key: this.actionKey(ACTION_OP_LOG, measure.regulId), label: 'Show Op Log' },
                { key: 'divider-2', type: 'divider' },
                { key: this.actionKey(ACTION_INTERACTION_STUDY, measure.regulId), label: 'Interaction study' },
                { key: 'divider-4', type: 'divider' },
                {
                    label: 'Create a custom layer',
                    key: this.actionKey(ACTION_CREATE_CUSTOM_LAYER, measure.regulId),
                },
                ...this.addToCustomLayerMenu(measure),
                ...this.removeFromCustomLayerMenu(measure.regulId),
                ...this.removeFromWorkspaceLayerMenu(measure.regulId),
            ],
        }));
        if (this.props.group) {
            return [
                {
                    label: <span className="detailed-subpanel-title">Measure ({this.props.measures.length})</span>,
                    key: 'measures',
                    children: subMenu,
                },
            ];
        } else {
            return subMenu;
        }
    };

    getMeasureCustomLayers(measureId: string) {
        return CustomLayerUtils.customLayers.filter(
            customLayer =>
                customLayer.objectsToDisplay.filter(
                    objectToDisplay =>
                        MapObjectType.MEASURE === objectToDisplay.type &&
                        measureId === MapLayerUtils.MONITORED_MEASURE_KEY_PROVIDER.getKey(objectToDisplay.object),
                )?.length > 0,
        );
    }

    getMeasureNameLabel(measure: NiaMeasureImpact) {
        const monitoredPeriod = AppContext.getMonitoringPeriod();
        return (
            <div style={{ width: '21em' }}>
                <Flex grow={false}>
                    <Flex size="6.5em">
                        ID: <span style={{ color: 'white' }}>{measure.regulId}</span>
                    </Flex>
                    <Flex size="15em" direction="vertical" style={{ alignSelf: 'center', paddingLeft: '1em' }}>
                        <MeasureLoadBar
                            key="regulation-load"
                            loadValues={measure.load}
                            drawBottomLine={false}
                            monitoredPeriod={monitoredPeriod}
                            currentTime={AppContext.getCurrentTime()}
                            barHeightEm={1}
                        />
                    </Flex>
                </Flex>
            </div>
        );
    }

    addToCustomLayerMenu(measure: NiaMeasureImpact) {
        return CustomLayerUtils.customLayers.map(customLayer => ({
            key: 'add-to-layer-' + customLayer.id + measure.regulId,
            label: `Add to: ${customLayer.label}`,
            onClick: () => this.addToCustomLayerMenuHandleUpdateSection(measure.regulId, customLayer),
        }));
    }

    removeFromCustomLayerMenu(measureId: string) {
        const measureCustomLayers = this.getMeasureCustomLayers(measureId);
        if (measureCustomLayers?.length > 0) {
            return [
                { key: 'divider-cl' + measureId, type: 'divider' },
                {
                    key: 'remove-from-layer-' + measureId,
                    label: 'Remove from custom layer',
                    children: [
                        ...measureCustomLayers.map(customLayer => ({
                            key: 'remove-from-layer-' + customLayer.id + measureId,
                            label: `Remove from: ${customLayer.label}`,
                            onClick: () => this.removeFromCustomLayerMenuHandleUpdateSection(measureId, customLayer),
                        })),
                    ],
                },
            ];
        }
        return [];
    }

    removeFromWorkspaceLayerMenu(measureId: string) {
        if (
            Utils.isExists(this.props.workspaceMeasureIds) &&
            Utils.contains(this.props.workspaceMeasureIds, measureId)
        ) {
            return [
                { key: 'divider-wkl' + measureId, type: 'divider' },
                {
                    key: 'remove-from-workspace-layer' + measureId,
                    label: 'Remove from workspace layer',
                    onClick: () => this.removeFromWorkspaceLayerMenuHandler(measureId),
                },
            ];
        }
        return [];
    }

    removeFromCustomLayerMenuHandleUpdateSection(measureId: string, customLayer: MapLayer) {
        this.props.onClose();
        RemoveFromCustomLayerEvent.fire(customLayer, measureId, MapObjectType.MEASURE);
    }

    removeFromWorkspaceLayerMenuHandler(measureId: string) {
        this.props.onClose();
        RemoveFromWorkspaceLayerEvent.fireRemoveMeasure(measureId);
    }

    actionKey(action: string, measureId: string) {
        return `${action}_${measureId}`;
    }

    handleOnClick = event => {
        const parsedKey = event.key.split('_');
        const action = parsedKey[0].trim();
        //check if is a valid action
        if (!ACTIONS.includes(action)) return;

        const measureId: string = parsedKey[1];
        //sanity check
        const measure = this.props.measures.find(measure => measure.regulId === measureId);

        //check if is a valid tv
        if (!measure) return;
        //close the context menu first
        this.props.onClose();

        switch (action) {
            case ACTION_OPEN_EDIT:
                ModalHandler.showRegulationEditor(measureId, 'reg');
                break;
            case ACTION_SLOT_LIST:
                ModalHandler.showSlotList({ id: measure.regulId, type: MeasureType.REGULATION });
                break;
            case ACTION_OP_LOG:
                ModalHandler.showMeasureOpLog({ id: measure.regulId, type: MeasureType.REGULATION });
                break;
            case ACTION_INTERACTION_STUDY:
                ModalHandler.showInteractionStudy({ id: measure.regulId, type: MeasureType.REGULATION });
                break;
            case ACTION_CREATE_CUSTOM_LAYER:
                this.showCustomLayerNamePopup(measure.tvId);
                break;
        }
    };

    addToCustomLayerMenuHandleUpdateSection(measureId: string, customLayer: MapLayer) {
        this.props.onClose();
        AddToCustomLayerEvent.fire(customLayer, measureId, MapObjectType.MEASURE);
    }

    showCustomLayerNamePopup(tvName: string) {
        const value: MapSelectionValues = Object.assign(
            {},
            {
                [MapLayerUtils.TV_MAP_SELECTION_POPUP_FIELD]: {
                    label: 'TFV',
                    options: [tvName],
                    selected: [tvName],
                },
            },
        );
        const content = <NewLayerPopup onSave={name => this.handleSaveNewLayer(value, name)} />;
        AppContext.eventBus.publish(new ShowModalEvent(ShowModalEvent.NAME, content, '200px'));
    }

    handleSaveNewLayer = (value: MapSelectionValues, name: string) => {
        const values: Map<MapObjectType, Array<string>> = new Map<MapObjectType, Array<string>>();
        values.set(MapObjectType.TV, value[MapLayerUtils.TV_MAP_SELECTION_POPUP_FIELD].selected);
        NewCustomLayerEvent.fire(name, values);
    };
}
